/**
 * Safely copies text to clipboard with proper error handling
 * @param {string} text - Text to copy to clipboard
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export async function copyToClipboard(text) {
  try {
    // Check if the Clipboard API is available
    if (!navigator?.clipboard?.writeText) {
      console.warn('Clipboard API is not available in this context');
      
      // Fallback to older execCommand method for older browsers
      return fallbackCopyToClipboard(text);
    }

    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    
    // Try fallback method if modern API fails
    return fallbackCopyToClipboard(text);
  }
}

/**
 * Fallback clipboard copy method using execCommand (for older browsers)
 * @param {string} text - Text to copy to clipboard
 * @returns {boolean} - Returns true if successful, false otherwise
 */
function fallbackCopyToClipboard(text) {
  try {
    // Create a temporary textarea element
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    
    document.body.appendChild(textarea);
    textarea.select();
    
    // Execute copy command
    const successful = document.execCommand('copy');
    document.body.removeChild(textarea);
    
    if (!successful) {
      console.warn('Fallback clipboard copy failed');
    }
    
    return successful;
  } catch (error) {
    console.error('Fallback clipboard copy error:', error);
    return false;
  }
}
